import { useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { useQueryClient } from "@tanstack/react-query";

import { describeApiError } from "../../api/errors";
import {
  useAllReviewItems,
  useControlTotals,
  useMigration,
  usePackageManifest,
  useResolveReviewItem,
  useReviewItems,
  useScope,
  useScorecard,
  useSignoffWatch,
} from "../../api/hooks";
import type { MigrationState } from "../../api/types";
import { Link } from "react-router-dom";
import { ActionsFirst, Banner, Button, Card, Disclosure, EmptyState, Spinner } from "../../components";
import { t } from "../../i18n/t";
import { MigrationPage } from "../migrationPage";
import { resolveMayMoveState } from "../review/gating";
import { OtherReviewItems } from "../review/OtherReviewItems";
import { isStagePast, stageForState } from "../stageRoute";
import { StepOutcome } from "../StepOutcome";
import { HmrcVerificationPanel } from "../status/HmrcVerificationPanel";
import { FxOpeningChecks } from "./FxOpeningChecks";
import { Concepts, type ConceptRow } from "./Concepts";
import { NotMigrated, type NotMigratedRow } from "./NotMigrated";
import { Scorecard, type ScoreRow } from "./Scorecard";
import { SignoffDialog } from "./SignoffDialog";
import { countChecks, describeCounts, isFailingStatus } from "./checkCounts";
import { TotalsTable, type TotalRow } from "./TotalsTable";

/** Distinct `point` values, in first-seen order (§A8.2: `point` is a free string, not an enum). */
export function pointsOf(rows: readonly TotalRow[]): string[] {
  const seen: string[] = [];
  for (const row of rows) {
    if (!seen.includes(row.point)) {
      seen.push(row.point);
    }
  }
  return seen;
}

export function DryRunScreen(): JSX.Element {
  const { migrationId } = useParams();
  const id = migrationId ?? "";
  const [runId, setRunId] = useState("");
  const migration = useMigration(id === "" ? null : id);
  const totals = useControlTotals(id, { phase: "dry_run", ...(runId === "" ? {} : { run_id: runId }) });
  const scorecard = useScorecard(id);
  const [signing, setSigning] = useState(false);
  // The totals section stays as it was while a refetch briefly leaves no rows: "open" follows the last loaded run.
  const totalsHadFailures = useRef(false);
  const signoffWatch = useSignoffWatch(id, migration.data?.state);
  const manifest = usePackageManifest(id);
  // xmg-webux (defect 6): the scope already lists the source bank accounts (`runtime.source_bank_accounts`),
  // so a bank row keyed by an account id can be shown by its name. Same query the sign-off dialog reads.
  const scope = useScope(id);
  const qc = useQueryClient();
  // FX opening-balance spec §3.7: "any", never the hook's "current" default -- FX_OPENING rows carry no
  // mapping version, and "current" hides every version-less row.
  const fxChecks = useReviewItems(id, { kind: "FX_OPENING", mapping_version: "any" });
  const resolveFxFinding = useResolveReviewItem(id);
  const [fxFailure, setFxFailure] = useState<string | null>(null);
  // xero-control-legs design Final decision 9: every version-less review item that is NOT one of the
  // four dedicated mapping panels and NOT FX_OPENING (its own dedicated panel above) -- today that is
  // SOURCE (the fixed-asset register/ledger finding this design adds, `journals_scope_not_granted`, ...),
  // VAT_LOCK, CHUNKING and HYPERCARE. `mapping_version: "any"` because these rows carry no mapping
  // version at all, the same reason `fxChecks` above does not use the hook's "current" default.
  const otherItems = useAllReviewItems(id, { mapping_version: "any" });
  const resolveOtherItem = useResolveReviewItem(id);
  const [otherFailure, setOtherFailure] = useState<string | null>(null);

  // §B6.1 / xmg-webux (defect 5): `list_control_totals`, `get_scorecard`, `get_package_manifest` and the
  // review-item lists are snapshots with no poll of their own. `MigrationPage`'s `useLiveMigrationRefresh`
  // invalidates every one of them (every query under this migration) when the polled state or the newest
  // event changes (xmg-webux2: not `updated_at`, which moves on every runtime write) -- e.g. `dry_running -> awaiting_conversion_signoff`, where `stage_dry_run` has just written the
  // totals and may have filed a SOURCE BLOCK item -- so this screen no longer keeps its own list.

  if (migration.isLoading) {
    return <Spinner />;
  }

  const state = (migration.data?.state ?? "draft") as MigrationState;
  const readOnly = stageForState(state) !== "dry-run";
  const currency = migration.data?.base_currency ?? "";
  const rows = (totals.data?.items ?? []) as unknown as readonly TotalRow[];
  const runIds = ((totals.data as { run_ids?: string[] } | undefined)?.run_ids ?? []) as readonly string[];
  // xmg-webux (defect 4): ONE definition of "failing" (FAIL/MISSING) for the note, the button, the tiles,
  // the group lines and the dialog -- a WARN is counted as a warning, never as a failure.
  const failing = rows.filter((row) => isFailingStatus(row.status));
  if (totals.data !== undefined) totalsHadFailures.current = failing.length > 0;
  // xmg-webux (defect 4): the conversion can be signed only while the migration is waiting for it.
  const signoffOpen = state === "awaiting_conversion_signoff";
  const bankNames = new Map(
    (scope.data?.source_bank_accounts ?? []).map((account) => [
      account.account_id,
      account.code === "" ? account.name : `${account.code} ${account.name}`,
    ]),
  );
  const scoreRows = (scorecard.data?.rows ?? []) as unknown as readonly ScoreRow[];
  const notMigrated = (scorecard.data?.not_migrated ?? []) as unknown as readonly NotMigratedRow[];
  const concepts = (scorecard.data?.concepts ?? []) as unknown as readonly ConceptRow[];
  const points = pointsOf(rows);
  const fxItems = fxChecks.data?.items ?? [];
  const fxBlocking = fxItems.filter((item) => item.severity === "BLOCK" && item.resolved_at === null).length;
  const isOpenItem = (item: { resolved_at?: string | null }): boolean =>
    item.resolved_at === null || item.resolved_at === undefined;
  // xmg-lmux (1): at the conversion sign-off an open VAT_LOCK notice is not a separate review item -- the
  // sign-off dialog shows it and signing acknowledges it (the server takes the acknowledgement with the
  // sign-off). It neither sits in the panel below nor disables the sign-off button.
  const otherRows = (otherItems.data ?? []).filter(
    (item) =>
      item.mapping_version_id === null &&
      item.kind !== "FX_OPENING" &&
      !(signoffOpen && item.kind === "VAT_LOCK" && isOpenItem(item)),
  );
  const otherBlocking = otherRows.filter((item) => item.severity === "BLOCK" && isOpenItem(item)).length;
  const vatLockInDialog = (otherItems.data ?? []).some(
    (item) => signoffOpen && item.kind === "VAT_LOCK" && isOpenItem(item),
  );

  async function onFxAcknowledge(reviewItemId: string, option: string): Promise<void> {
    try {
      await resolveFxFinding.mutateAsync({
        itemId: reviewItemId,
        body: { option },
        mayMoveState: resolveMayMoveState(state, option),
      });
    } catch (caught) {
      setFxFailure(describeApiError(caught, t("error.unexpected")));
    }
  }

  async function onOtherResolve(reviewItemId: string, option: string): Promise<void> {
    try {
      await resolveOtherItem.mutateAsync({
        itemId: reviewItemId,
        body: { option },
        mayMoveState: resolveMayMoveState(state, option),
      });
      // `useResolveReviewItem`'s own `onSuccess` invalidates the "review-items" prefix, not
      // "review-items-all" -- invalidated explicitly here so this panel (and the blocking count) refetch.
      void qc.invalidateQueries({ queryKey: ["migration", id, "review-items-all"] });
    } catch (caught) {
      setOtherFailure(describeApiError(caught, t("error.unexpected")));
    }
  }

  const pointSummary = points.map((point) => ({
    point,
    ...countChecks(rows.filter((row) => row.point === point)),
  }));

  // xmg-actions-first: what needs the operator (open questions) leads; the data sits below, collapsed.
  // An INFO line (a figure we filled in) is a note, not an open question: it never leads the screen.
  const fxOpen = fxItems.filter((item) => isOpenItem(item) && item.severity !== "INFO");
  const hasQuestions =
    fxOpen.length > 0 || otherRows.length > 0 || fxFailure !== null || otherFailure !== null;
  const answersNeeded = fxBlocking + otherBlocking;
  const totalCounts = countChecks(rows);
  const notFullyMigrated = scoreRows.filter((row) => row.support !== "FULL").length;
  const otherPanelProps = {
    items: otherRows,
    disabled: readOnly || resolveOtherItem.isPending,
    onResolve: (itemId: string, option: string) => void onOtherResolve(itemId, option),
  };

  const questions = !hasQuestions ? null : (
    <>
      {fxFailure === null ? null : <Banner tone="block">{fxFailure}</Banner>}
      {otherFailure === null ? null : <Banner tone="block">{otherFailure}</Banner>}
      {fxOpen.length === 0 ? null : (
        <FxOpeningChecks
          items={fxItems}
          saving={resolveFxFinding.isPending}
          baseCurrency={currency}
          onAcknowledge={(reviewItemId, option) => void onFxAcknowledge(reviewItemId, option)}
        />
      )}
      {/*
        xero-control-legs design Final decision 9: the SAME generic panel `ReviewScreen` uses for every
        `kind` with no dedicated tab -- SOURCE (the fixed-asset register/ledger finding this design adds,
        `journals_scope_not_granted`, ...), VAT_LOCK, CHUNKING, HYPERCARE -- rendered here too, because
        every one of these is filed BEFORE `awaiting_conversion_signoff`'s own review stage exists and
        `ReviewScreen` (the `review` stage only) is `readOnly` here, with no control to resolve them. An
        open BLOCK among them disables sign-off, exactly like FX_OPENING. xmg-actions-first: only the
        questions are up here; the notes and answered rows are in the collapsed "Other items" below.
      */}
      {otherRows.length === 0 ? null : (
        <div className="afstack" data-testid="dryrun-other-review-items">
          <OtherReviewItems {...otherPanelProps} />
        </div>
      )}
    </>
  );

  const action = signoffOpen ? (
    <Card title={t("dryrun.signoff_title")}>
      <p
        className={answersNeeded > 0 ? "afaction__status afaction__status--block" : "afaction__status"}
        data-testid="dryrun-answers-needed"
      >
        {answersNeeded === 0
          ? t("dryrun.ready_to_sign")
          : answersNeeded === 1
            ? t("dryrun.answers_needed_one")
            : t("dryrun.answers_needed_many", { count: answersNeeded })}
      </p>
      {failing.length === 0 ? null : (
        <p className="countbox">
          <span className="cellwrap">{t("dryrun.signoff_warn_note", { count: failing.length })}</span>
        </p>
      )}
      {/*
          §B5.5: sign-off is offered even when checks fail — the state machine allows it and an operator may
          knowingly proceed. Silently disabling it would hide a decision the audit trail exists to record. The
          label carries the count and the button is `destructive`, which is colour **and** words (§B7.3).
          FX opening-balance spec §3.7 is one EXCEPTION: an open FX_OPENING BLOCK is not advisory -- the
          server itself refuses conversion sign-off while one is open (`_signoff_precondition`,
          `runner.signoff`) -- so the button is disabled rather than offered-with-a-warning. xero-control-legs
          design Final decision 9 adds a SECOND: any other open BLOCK review item (SOURCE/VAT_LOCK/CHUNKING/
          HYPERCARE) is exactly the same kind of server-side refusal (`repo.open_blocking_reviews`).
        */}
      <Button
        variant={failing.length > 0 ? "destructive" : "primary"}
        className="button--auth"
        disabled={fxBlocking > 0 || otherBlocking > 0 || signoffWatch.waiting}
        onClick={() => setSigning(true)}
      >
        {signoffWatch.waiting
          ? t("signoff.working")
          : failing.length > 0
            ? t("signoff.open_with_failures", { count: failing.length })
            : t("signoff.open")}
      </Button>
      {signoffWatch.stalled ? (
        <span className="muted" role="status" data-testid="signoff-stalled">
          {t("signoff.still_processing")}
        </span>
      ) : null}
      {vatLockInDialog ? (
        <p className="muted" data-testid="dryrun-vat-lock-in-dialog">
          {t("dryrun.vat_lock_in_dialog")}
        </p>
      ) : null}
      <Link className="backlink backlink--center" to={`/migrations/${id}/review`}>
        {t("dryrun.review_again")}
      </Link>
    </Card>
  ) : (
    <StepOutcome
      id={id}
      state={state}
      title={t("dryrun.signoff_title")}
      before={!isStagePast(state, "dry-run")}
    />
  );

  return (
    <MigrationPage
      id={id}
      migration={migration.data}
      title={t("dryrun.title")}
      actions={
        runIds.length > 1 ? (
          <label className="runselect">
            {t("dryrun.run_select")}
            <select value={runId} onChange={(event) => setRunId(event.target.value)}>
              <option value="">{t("dryrun.run_latest")}</option>
              {runIds.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </label>
        ) : undefined
      }
    >
      {readOnly ? <Banner tone="info">{t("dryrun.read_only")}</Banner> : null}

      <ActionsFirst
        questions={questions}
        action={action}
        actionLabel={t("dryrun.signoff_title")}
        pinned={signoffOpen}
      >
        <HmrcVerificationPanel migrationId={id} />

        {/* Not migrated, and why: stays on the page, never collapsed (§B5.3) -- it is short, and the dialog
            repeats it. */}
        <NotMigrated rows={notMigrated} />
        <Concepts rows={concepts} />

        {/* FX checks with nothing left to answer: the "all clear" line, not a question. */}
        {fxOpen.length > 0 ? null : (
          <FxOpeningChecks
            items={fxItems}
            saving={resolveFxFinding.isPending}
            baseCurrency={currency}
            onAcknowledge={(reviewItemId, option) => void onFxAcknowledge(reviewItemId, option)}
          />
        )}

        {rows.length === 0 ? (
          <EmptyState title={t("dryrun.empty")} />
        ) : (
          <Disclosure
            title={t("dryrun.totals_section")}
            counts={describeCounts(totalCounts)}
            open={totalsHadFailures.current ? true : undefined}
            attention={failing.length > 0}
            testId="dryrun-totals"
          >
            {pointSummary.length === 0 ? null : (
              <section className="tiles tiles--3" aria-label={t("dryrun.comparison_label")}>
                {pointSummary.map((point) => {
                  const warn = point.warn;
                  const total = point.pass + point.fail + warn;
                  return (
                    <div className="pt" key={point.point} data-testid={`point-tile-${point.point}`}>
                      <span className="eyebrow">{point.point}</span>
                      <div className="pt__numbers">
                        <span className="pt__n">{point.pass}</span>
                        <span className="pt__label">{t("dryrun.tile_pass")}</span>
                        <span className={point.fail > 0 ? "pt__n pt__n--fail" : "pt__n pt__n--zero"}>
                          {point.fail}
                        </span>
                        <span className={point.fail > 0 ? "pt__label--fail" : "pt__label"}>
                          {t("dryrun.tile_fail")}
                        </span>
                        {warn === 0 ? null : (
                          <>
                            <span className="pt__n pt__n--warn">{warn}</span>
                            <span className="pt__label--warn">{t("dryrun.tile_warn")}</span>
                          </>
                        )}
                      </div>
                      {total === 0 ? null : (
                        <div className="pt__bar" aria-hidden="true">
                          <span className="pt__seg--pass" style={{ flex: `${point.pass} 1 0%` }} />
                          {point.fail === 0 ? null : (
                            <span className="pt__seg--fail" style={{ flex: `${point.fail} 1 0%` }} />
                          )}
                          {warn === 0 ? null : (
                            <span className="pt__seg--warn" style={{ flex: `${warn} 1 0%` }} />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </section>
            )}
            {points.map((point) => (
              <TotalsTable
                key={point}
                point={point}
                currency={currency}
                rows={rows.filter((row) => row.point === point)}
                bankNames={bankNames}
              />
            ))}
          </Disclosure>
        )}

        <Disclosure
          title={t("dryrun.scorecard")}
          counts={
            scoreRows.length === 0
              ? undefined
              : notFullyMigrated === 0
                ? t("dryrun.scorecard_section_counts_all", { n: scoreRows.length })
                : t("dryrun.scorecard_section_counts", { n: scoreRows.length, partial: notFullyMigrated })
          }
          testId="dryrun-scorecard"
        >
          <Scorecard rows={scoreRows} hideHeading />
        </Disclosure>

        {signoffOpen ? (
          <Disclosure title={t("dryrun.signing_section")} testId="dryrun-signing-scope">
            <dl className="preview__dl">
              <dt>{t("signoff.package_sha256")}</dt>
              <dd className="mono cellwrap">{manifest.data?.package_sha256 ?? t("signoff.hash_pending")}</dd>
              <dt>{t("signoff.scope_sha256")}</dt>
              <dd className="mono cellwrap">{manifest.data?.scope_sha256 ?? t("common.none")}</dd>
              <dt>{t("signoff.mapping_version")}</dt>
              <dd className="mono">{manifest.data?.mapping_version_id ?? t("common.none")}</dd>
            </dl>
          </Disclosure>
        ) : null}
      </ActionsFirst>

      <SignoffDialog
        migrationId={id}
        open={signing && signoffOpen}
        onCancel={() => setSigning(false)}
        failingChecks={failing}
        notMigrated={notMigrated}
        pointSummary={pointSummary}
        bankNames={bankNames}
      />
    </MigrationPage>
  );
}
